/**
 * Pick the RudderStack account a warehouse connection should use (phase 3d).
 *
 * **Nothing is ever submitted by a default.** The highlighted row is a ranking affordance;
 * the primary button names the account it would bind ("Adopt `<name>`") so the admin cannot commit
 * without having read which account it is. `rs_account_adopted_by` has to be able to claim a human
 * chose this, and it cannot if a default could have chosen it.
 *
 * **A coordinate mismatch is shown, not hidden.** The server refuses it, the drawer explains which
 * coordinate differs, and only then offers to proceed — which sends `acknowledgeMismatch` and is
 * recorded as acknowledged. The two VALUES are never in the error payload; the screen
 * already has them and the log must not.
 */
import { AlertTriangle, Check, X } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/_shared/components/ui/sheet';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';

import { useWarehousePermission } from '../accountCreatePermission';
import { PermissionNotice } from '../PermissionNotice';
import { refusalMessage, refusalProblem } from '../refusalCopy';
import { canonicalWarehouseTypeKey } from '../warehouseTypes';
import { accountDisplayName, accountRowIdentity, adoptRefusal, highlightedAccountId } from './adoptPresentation';
import type { AdoptAccountsHook, RsAccountOverview } from './useAdoptAccounts';

export interface AdoptTarget {
  /** The connection being bound. */
  connectionKey: string;
  /** Its display label, for the drawer's header. */
  connectionName: string;
  /** Its warehouse type, to narrow the candidate list to the same engine. */
  type: string;
}

function CandidateRow({
  account,
  selected,
  highlighted,
  onSelect,
}: {
  account: RsAccountOverview;
  selected: boolean;
  highlighted: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const { title, detail } = accountRowIdentity(account);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-grey-25'
      }`}
    >
      <span className="flex size-4 shrink-0 items-center justify-center pt-1">
        {selected && <Check className="size-4 text-primary" />}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          {highlighted && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Most used
            </Badge>
          )}
          {account.lookoutOwned && (
            <Badge variant="outline" className="shrink-0 text-xs">
              By Lookout
            </Badge>
          )}
          {account.state !== 'not_opted_in' && (
            <Badge variant="outline" className="shrink-0 text-xs">
              In use
            </Badge>
          )}
        </span>
        {detail !== null && (
          <span className="font-mono text-xs wrap-break-word whitespace-normal text-muted-foreground/70">{detail}</span>
        )}
      </span>
    </button>
  );
}

/**
 * The drawer, which owns only the open/closed state.
 *
 * The body is REMOUNTED per connection (`key`), and that is the whole reason the split exists: a
 * selection carried over from the last connection would be a default nobody chose, which is exactly
 * what "nothing is submitted by a default" rules out. A `useEffect` that reset the state on `connectionKey` would do the same job
 * one render later and through a cascading re-render; the key does it before anything is painted.
 */
export function AdoptAccountDrawer({
  target,
  hook,
  canAct,
  onOpenChange,
}: {
  target: AdoptTarget | null;
  hook: AdoptAccountsHook;
  canAct: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  // The refusal lives on the mutation, which outlives the body's remount, so closing the drawer has
  // to clear it explicitly. Without this the next connection you open starts with the previous
  // connection's refusal already on screen, attached to an account it was never about.
  const handleOpenChange = React.useCallback(
    (open: boolean): void => {
      if (!open) hook.resetAdopt();
      onOpenChange(open);
    },
    [hook, onOpenChange],
  );

  return (
    <Sheet open={target !== null} onOpenChange={handleOpenChange}>
      <SheetContent side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-drawer">
        <AdoptAccountBody
          key={target?.connectionKey ?? 'none'}
          target={target}
          hook={hook}
          canAct={canAct}
          onOpenChange={handleOpenChange}
        />
      </SheetContent>
    </Sheet>
  );
}

function AdoptAccountBody({
  target,
  hook,
  canAct,
  onOpenChange,
}: {
  target: AdoptTarget | null;
  hook: AdoptAccountsHook;
  canAct: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const permission = useWarehousePermission();

  const candidates = React.useMemo(() => {
    if (target === null) return [];
    // **Canonical on BOTH sides, never raw.** The target's `type` is the DRIVER string the connection
    // was saved with (`BIGQUERY`, `REDSHIFT`, `DELTALAKE`); the overview's is the server's canonical
    // type (`BQ`, `RS`, `DATABRICKS`). Comparing them raw dropped every BigQuery candidate — 849 of
    // prod's 893 warehouse accounts — and did it silently, as an empty list that reads like "you have
    // no accounts". Folding both through one function is what makes the two spellings unable to
    // disagree; `AdoptAccountDrawer.test.tsx` walks every alias so neither side can drift alone.
    const wanted = canonicalWarehouseTypeKey(target.type);
    // Same engine only. The server checks it too (`type_mismatch`) — this just keeps the list honest
    // rather than offering an account that is guaranteed to be refused.
    return hook.accounts.filter(
      (a) => a.type === 'unknown' || (wanted !== null && canonicalWarehouseTypeKey(a.type) === wanted),
    );
  }, [hook.accounts, target]);

  const highlighted = React.useMemo(() => highlightedAccountId(candidates), [candidates]);
  const selected = candidates.find((a) => a.accountId === selectedId) ?? null;
  const refusal = adoptRefusal(hook.adoptError);
  const mismatch = refusal?.reason === 'coordinate_mismatch';
  const adoptProblem = refusalProblem(hook.adoptError);

  const submit = (): void => {
    if (target === null || selected === null) return;
    hook.adopt({
      connectionKey: target.connectionKey,
      accountId: selected.accountId,
      ...(acknowledged ? { acknowledgeMismatch: true } : {}),
    });
  };

  return (
    <>
      <SheetHeader className="border-b border-border p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="flex min-w-0 flex-col">
            <SheetTitle className="truncate text-sm font-medium text-foreground">
              Choose a RudderStack account
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              {target === null
                ? ''
                : `${target.connectionName} will use this account's credential. Lookout reads it — the account stays yours to manage in RudderStack, and Lookout cannot rotate, revoke or edit it.`}
            </SheetDescription>
          </span>
          <SheetClose asChild>
            <Button type="button" variant="tertiary" iconOnly aria-label="Close" Icon={<X />} />
          </SheetClose>
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {!hook.adoptionEnabled && (
          // No copy existed for this before. Adoption is refused server-side as
          // `read_flag_off`, and a screen that lets an admin try anyway is a screen that teaches
          // them the feature is broken.
          <Callout variant="warning">
            Lookout cannot read credentials from RudderStack for this workspace yet, so adopting an account now would
            leave this connection unable to run queries. Ask your RudderStack contact to enable it first.
          </Callout>
        )}

        {hook.unavailableProblem !== undefined && <WarehouseProblemCallout problem={hook.unavailableProblem} />}

        {hook.adoptError !== null &&
          hook.adoptError !== undefined &&
          (!mismatch && adoptProblem ? (
            <WarehouseProblemCallout problem={adoptProblem} />
          ) : (
            <Callout variant={mismatch ? 'warning' : 'error'}>
              {mismatch
                ? `That account names a different ${refusal?.coordinate ?? 'warehouse'} than this connection. Check it is the one you mean before continuing.`
                : refusalMessage(hook.adoptError, 'That account cannot be used for this connection')}
            </Callout>
          ))}

        {/* A list that failed to load says so above; "no account" would not be true. */}
        {candidates.length === 0 && hook.unavailableProblem !== undefined ? null : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            This workspace has no RudderStack account for this warehouse type. Create one in RudderStack first.
          </p>
        ) : (
          <div role="radiogroup" aria-label="RudderStack accounts" className="grid gap-2">
            {candidates.map((account) => (
              <CandidateRow
                key={account.accountId}
                account={account}
                selected={account.accountId === selectedId}
                highlighted={account.accountId === highlighted}
                onSelect={() => {
                  setSelectedId(account.accountId);
                  setAcknowledged(false);
                  hook.resetAdopt();
                }}
              />
            ))}
          </div>
        )}

        {mismatch && selected !== null && (
          <label className="flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1"
            />
            <span className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
              Use it anyway. Lookout will run this connection’s queries against the account’s warehouse.
            </span>
          </label>
        )}
      </div>

      {!canAct && <PermissionNotice permission={permission} className="mx-4 mb-4" />}
      <div className="flex items-center justify-end gap-2 border-t border-border p-4">
        <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} label="Cancel" />
        <Button
          type="button"
          variant="primary"
          loading={hook.adoptPending}
          // Named, never generic: the button is the last thing between a default and the audit
          // column, so it has to say which account it binds.
          label={selected === null ? 'Adopt account' : `Adopt ${accountDisplayName(selected)}`}
          disabled={!canAct || selected === null || !hook.adoptionEnabled || (mismatch && !acknowledged)}
          onClick={submit}
        />
      </div>
    </>
  );
}
